import { LANGUAGES, TASK_KIND_LABEL, isLanguageCode, type JobConfig } from '@eduvy/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Icon, type IconName } from '../components/icons';
import { Avatar, Badge, Button, Callout, Card, Checkbox, CopyButton, ErrorNote, Input, Loading, Menu, Meta, Modal, PageHeader, ProgressBar, Ring, Segmented, Textarea, cx } from '../components/ui';
import { useAuth } from '../lib/auth';
import { api, type CandidateDetail, type DecisionKind, type InterviewDetail, type Scorecard, type SkillsReport, type Stage, type Verdict } from '../lib/api';
import { CANDIDATE_STATUS, CHANNEL_LABEL, INTERVIEW_STATUS, RECOMMENDATION, VERDICT, formatDateTime, formatDuration, formatPhone, scoreText, titleCase } from '../lib/format';
import { activeTurnSeq, buildClipSegments, computeMarkers, locateSegment, timelineDuration, turnOffset, type Marker } from '../lib/replay';

const VERDICT_PILL: Record<Verdict, string> = {
  QUALIFIED: 'bg-success-soft text-success ring-success/25',
  NEEDS_REVIEW: 'bg-warning-soft text-warning ring-warning/25',
  NOT_QUALIFIED: 'bg-destructive-soft text-destructive ring-destructive/25',
};

const MARKER_STYLE: Record<Marker['kind'], { colour: string; label: string }> = {
  consent: { colour: '#4ade80', label: 'Consent' },
  evidence: { colour: '#f7a6cf', label: 'Evidence' },
  concern: { colour: '#fbbf24', label: 'Concern' },
  flag: { colour: '#fb7185', label: 'Red flag' },
};

type SeekRequest = { interviewId: string; seq: number; n: number };

// livekit-client loads only when a recruiter opens a meeting.
const MeetingPanel = lazy(() => import('./MeetingPanel'));

const STAGE_LABEL: Record<Stage, string> = { SCREENING: 'AI interview', ASSESSMENT: 'Skills assessment', RECRUITER: 'Recruiter interview' };
const STAGE_ICON: Record<Stage, IconName> = { SCREENING: 'headphones', ASSESSMENT: 'target', RECRUITER: 'users' };
const OPEN: InterviewDetail['status'][] = ['PENDING', 'INVITED', 'RINGING', 'IN_PROGRESS'];
const POLL_STATUSES = ['QUEUED', 'INVITED', 'CALLING', 'IN_INTERVIEW', 'INTERVIEWED', 'ASSESSMENT_INVITED', 'IN_ASSESSMENT', 'MEETING_INVITED'];

const DECISION: Record<DecisionKind, { label: string; tone: 'indigo' | 'red' | 'amber' | 'green' | 'blue'; help?: string }> = {
  SHORTLIST: { label: 'Shortlist', tone: 'indigo', help: "When the job has a skills assessment, this plans the candidate's tasks and emails them the assessment link with the rules." },
  ADVANCE: { label: 'Invite to recruiter interview', tone: 'blue', help: "The candidate gets a link to book a time from the recruiters' availability, then joins the video interview from it." },
  SELECT: { label: 'Select', tone: 'green', help: 'The final yes. Your team still sends the offer itself.' },
  REJECT: { label: 'Reject', tone: 'red', help: 'Any link still open is withdrawn.' },
  HOLD: { label: 'Put on hold', tone: 'amber', help: 'Any link still open is withdrawn.' },
};

export function CandidatePage() {
  const { candidateId } = useParams() as { candidateId: string };
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => api.get<CandidateDetail>(`/api/candidates/${candidateId}`),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s && POLL_STATUSES.includes(s) ? 5000 : false;
    },
  });
  const [decision, setDecision] = useState<DecisionKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [replayId, setReplayId] = useState<string | null>(null);
  const [seek, setSeek] = useState<SeekRequest | null>(null);
  const [meetingGrant, setMeetingGrant] = useState<{ grant: MeetingGrant; interviewId: string } | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error ?? 'Not found'} />;
  const c = q.data;
  // Interviews come newest first; each stage's latest attempt stands for that stage.
  const scored = c.interviews.find((i) => i.stage === 'SCREENING' && i.scorecard) ?? null;
  const card = scored?.scorecard ?? null;
  const assessment = c.interviews.find((i) => i.stage === 'ASSESSMENT') ?? null;
  const meeting = c.interviews.find((i) => i.stage === 'RECRUITER') ?? null;
  const meetingOpen = meeting && OPEN.includes(meeting.status) ? meeting : null;
  const status = CANDIDATE_STATUS[c.status];
  const refresh = () => qc.invalidateQueries({ queryKey: ['candidate', candidateId] });
  const defaultReplay = (assessment?.scorecard ? assessment : null) ?? scored ?? c.interviews.find((i) => i.stage !== 'RECRUITER' && (i.turns.length > 0 || i.recordingUrl)) ?? null;
  const replay = c.interviews.find((i) => i.id === replayId) ?? defaultReplay;
  const primary: DecisionKind | null = ['ASSESSMENT_INVITED', 'IN_ASSESSMENT', 'ASSESSED'].includes(c.status) ? 'ADVANCE' : c.status === 'MEETING_INVITED' ? 'SELECT' : c.status === 'SELECTED' ? null : 'SHORTLIST';
  const pending = c.interviews.some((i) => ['PENDING', 'INVITED', 'RINGING'].includes(i.status));

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  const openMeeting = (interviewId: string) =>
    act(async () => {
      const grant = await api.post<MeetingGrant>(`/api/interviews/${interviewId}/meeting`);
      setMeetingGrant({ grant, interviewId });
    });

  const jumpToTurn = (interviewId: string, seq: number) => {
    setReplayId(interviewId);
    setSeek((prev) => ({ interviewId, seq, n: (prev?.n ?? 0) + 1 }));
    document.getElementById('replay')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const admin = user?.role === 'ADMIN';
  const more: Array<{ label: string; icon?: IconName; onSelect: () => void; danger?: boolean; hint?: string } | 'divider'> = [];
  if (card) more.push({ label: 'Re-score latest interview', icon: 'refresh', onSelect: () => void act(() => api.post(`/api/candidates/${c.id}/rescore`)) });
  if (pending) more.push({ label: 'Cancel pending interview', icon: 'x', onSelect: () => void act(() => api.post(`/api/candidates/${c.id}/cancel`)) });
  if (admin) {
    if (more.length) more.push('divider');
    more.push(
      {
        label: 'Reset candidate',
        icon: 'refresh',
        hint: 'Back to New; interviews and scores deleted',
        onSelect: () => {
          if (window.confirm(`Reset ${c.name}? Every interview, transcript, recording, score and decision is deleted and they go back to New, ready to start again. Their lead details stay.`)) {
            void act(() => api.post(`/api/candidates/${c.id}/reset`));
          }
        },
      },
      {
        label: 'Erase personal data',
        icon: 'shield',
        danger: true,
        onSelect: () => {
          if (window.confirm(`Erase all personal data, recordings and transcripts for ${c.name}? This cannot be undone.`)) {
            void act(() => api.del(`/api/candidates/${c.id}/personal-data`));
          }
        },
      },
      {
        label: 'Delete candidate',
        icon: 'trash',
        danger: true,
        onSelect: () => {
          if (window.confirm(`Delete ${c.name} from ${c.job.title}? The lead and everything recorded about them is removed. This cannot be undone.`)) {
            void act(async () => {
              await api.del(`/api/candidates/${c.id}`);
              await qc.invalidateQueries({ queryKey: ['candidates', c.job.id] });
              navigate(`/jobs/${c.job.id}`);
            });
          }
        },
      },
    );
  }

  return (
    <div>
      <PageHeader
        back={{ to: `/jobs/${c.job.id}`, label: c.job.title }}
        title={
          <span className="flex items-center gap-4">
            <Avatar name={c.name} size="lg" ring />
            <span>{c.name}</span>
          </span>
        }
        aside={
          <Badge tone={status.tone} dot>
            {status.label}
          </Badge>
        }
        meta={
          <>
            <Meta icon="phone">{formatPhone(c.phoneE164)}</Meta>
            {c.email && <Meta icon="mail">{c.email}</Meta>}
            {c.experienceRaw && <Meta icon="briefcase">{c.experienceRaw} yrs on sheet</Meta>}
            {c.sector && <Meta icon="tag">{c.sector}</Meta>}
          </>
        }
        actions={
          !c.erasedAt ? (
            <>
              {more.length > 0 && <Menu label="More actions" items={more} />}
              <Button icon="link" onClick={() => setShareOpen(true)} title="A read-only report for a hiring manager, no login needed">
                Share
              </Button>
              <Button icon="pause" onClick={() => setDecision('HOLD')}>
                Hold
              </Button>
              <Button icon="x" className="!text-destructive" onClick={() => setDecision('REJECT')}>
                Reject
              </Button>
              {meetingOpen && (
                <Button icon="video" loading={busy} onClick={() => void openMeeting(meetingOpen.id)}>
                  {meetingOpen.status === 'IN_PROGRESS' ? 'Join the meeting' : 'Open the meeting'}
                </Button>
              )}
              {primary && (
                <Button variant="primary" icon="arrowRight" onClick={() => setDecision(primary)}>
                  {DECISION[primary].label}
                </Button>
              )}
            </>
          ) : undefined
        }
      />
      <StageStrip candidate={c} />
      <ErrorNote error={actionError} className="mb-4" />
      {c.erasedAt && (
        <Callout tone="info" icon="shield" className="mb-5">
          Personal data was erased on {formatDateTime(c.erasedAt)}.
        </Callout>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-5">
          {replay ? (
            <ReplayCard interview={replay} card={replay.scorecard} seek={seek} />
          ) : (
            <Card title="Replay">
              <NoInterviewNote candidate={c} />
            </Card>
          )}
          {c.interviews.length > 0 && <AttemptsCard interviews={c.interviews} current={replay?.id ?? null} onReplay={setReplayId} />}
        </div>

        <div className="min-w-0 space-y-5">
          {meeting && (
            <Card title="Recruiter interview">
              <p className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone={INTERVIEW_STATUS[meeting.status].tone} dot>
                  {INTERVIEW_STATUS[meeting.status].label}
                </Badge>
                <span className="text-muted-fg">{meeting.scheduledAt ? `Booked for ${formatDateTime(meeting.scheduledAt)}` : OPEN.includes(meeting.status) ? 'Not booked yet' : ''}</span>
              </p>
              {meetingOpen && <p className="mt-2 text-xs leading-relaxed text-muted-fg">Open the meeting at the booked time; the candidate joins from the link in their email. The meeting stays open until you end it.</p>}
            </Card>
          )}
          {assessment && <SkillsView interview={assessment} onJump={(seq) => jumpToTurn(assessment.id, seq)} />}
          {card && scored ? (
            <ScorecardView card={card} candidate={c} onJump={(seq) => jumpToTurn(scored.id, seq)} />
          ) : (
            <Card title="AI interview">
              <NoInterviewNote candidate={c} />
            </Card>
          )}

          <Card title="Decisions" description="The AI verdict informs; the hiring team decides.">
            {c.decisions.length === 0 ? (
              <p className="text-sm text-muted-fg">No decision yet.</p>
            ) : (
              <ol className="relative space-y-4 border-l border-border pl-5 text-sm">
                {c.decisions.map((d) => (
                  <li key={d.id} className="relative">
                    <span className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full bg-accent ring-4 ring-surface" aria-hidden />
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={DECISION[d.kind]?.tone ?? 'slate'}>{DECISION[d.kind]?.label ?? titleCase(d.kind.toLowerCase())}</Badge>
                      <span className="text-xs text-muted-fg">
                        {d.byUser.name} · {formatDateTime(d.createdAt)}
                      </span>
                    </div>
                    {d.reason && <p className="mt-1 text-fg-2">{d.reason}</p>}
                  </li>
                ))}
              </ol>
            )}
          </Card>

          <Card title="Lead details">
            <dl className="divide-y divide-border text-sm">
              <Detail label="Phone" value={formatPhone(c.phoneE164)} extra={c.phoneRaw && c.phoneRaw !== c.phoneE164 ? `sheet: ${c.phoneRaw}` : undefined} />
              <Detail label="Email" value={c.email ?? '—'} />
              <Detail label="Experience (sheet)" value={c.experienceRaw ? `${c.experienceRaw} years` : '—'} />
              <Detail label="Sector" value={c.sector ?? '—'} />
              <Detail label="Source" value={c.source ?? '—'} />
              <Detail label="Language" value={c.preferredLang && isLanguageCode(c.preferredLang) ? LANGUAGES[c.preferredLang].label : 'Chosen at interview'} />
              {Object.entries(c.extra ?? {}).map(([k, v]) => (
                <Detail key={k} label={k} value={v} />
              ))}
            </dl>
            {(c.preScreen?.flags.length ?? 0) > 0 && (
              <Callout tone="warning" icon="flag" title="Import flags" className="mt-4">
                <ul className="list-inside list-disc space-y-0.5 text-xs">
                  {c.preScreen!.flags.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </Callout>
            )}
          </Card>
        </div>
      </div>

      <DecisionDialog
        kind={decision}
        hasAssessment={Boolean(c.job.config.assessment)}
        messages={c.job.config.messages}
        email={c.email}
        onClose={() => setDecision(null)}
        onSubmit={(reason, notify) =>
          act(() => api.post(`/api/candidates/${c.id}/decisions`, { kind: decision, reason: reason || undefined, ...(notify === undefined ? {} : { notify }) })).then(() => setDecision(null))
        }
      />
      <ShareDialog candidateId={c.id} name={c.name} open={shareOpen} onClose={() => setShareOpen(false)} />
      {meetingGrant && (
        <Suspense fallback={null}>
          <MeetingPanel
            grant={meetingGrant.grant}
            interviewId={meetingGrant.interviewId}
            onClose={() => {
              setMeetingGrant(null);
              void refresh();
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

type MeetingGrant = import('./MeetingPanel').MeetingGrant;

/** A read-only report link for a hiring manager: no login, no phone or email, valid for a set number of days. */
function ShareDialog({ candidateId, name, open, onClose }: { candidateId: string; name: string; open: boolean; onClose: () => void }) {
  const [days, setDays] = useState<'7' | '14' | '30'>('14');
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    api
      .post<{ url: string; expiresAt: string }>(`/api/candidates/${candidateId}/share`, { days: Number(days) })
      .then((r) => !cancelled && setLink(r))
      .catch((err: unknown) => !cancelled && setError(err))
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, [open, days, candidateId]);
  return (
    <Modal open={open} onClose={onClose} title={`Share ${name.split(' ')[0]}'s report`} description="Anyone with the link can read the scores, the notes and your decisions, without signing in. It carries no phone number or email." footer={<Button onClick={onClose}>Done</Button>}>
      <div className="space-y-4">
        <Segmented
          label="Valid for"
          size="md"
          value={days}
          onChange={setDays}
          options={[
            { value: '7', label: '7 days' },
            { value: '14', label: '14 days' },
            { value: '30', label: '30 days' },
          ]}
        />
        <ErrorNote error={error} />
        <div className="flex flex-wrap items-center gap-2">
          <Input readOnly value={busy ? 'Making the link…' : (link?.url ?? '')} className="min-w-0 flex-1 font-mono !text-xs" leading={<Icon name="link" className="h-4 w-4" />} />
          {link && !busy && (
            <CopyButton text={link.url} size="md" variant="primary">
              Copy link
            </CopyButton>
          )}
        </div>
        {link && !busy && <p className="text-xs text-muted-fg">Valid until {formatDateTime(link.expiresAt)}. Making a new link does not cancel an old one before it expires.</p>}
      </div>
    </Modal>
  );
}

/** Where the candidate is across the three rounds. */
function StageStrip({ candidate }: { candidate: CandidateDetail }) {
  const latest = (stage: Stage) => candidate.interviews.find((i) => i.stage === stage);
  const steps = (['SCREENING', 'ASSESSMENT', 'RECRUITER'] as const).map((stage, n) => {
    const i = latest(stage);
    const sc = i?.scorecard;
    const live = i?.status === 'IN_PROGRESS';
    const note = !i ? 'Not started' : sc ? `Score ${sc.overallScore} · ${VERDICT[sc.verdict].label}` : live ? 'In progress now' : i.scheduledAt && OPEN.includes(i.status) ? `Booked ${formatDateTime(i.scheduledAt)}` : INTERVIEW_STATUS[i.status].label;
    return { stage, n: n + 1, note, active: Boolean(i), done: Boolean(sc) || i?.status === 'COMPLETED', live, score: sc?.overallScore ?? null };
  });
  return (
    <ol className="mb-6 grid gap-3 sm:grid-cols-3" aria-label="Hiring stages">
      {steps.map((s) => (
        <li key={s.stage} className={cx('relative flex items-center gap-3.5 rounded-lg px-4 py-3.5 ring-1 transition', s.active ? 'surface' : 'bg-surface-2/60 ring-border')}>
          <span className={cx('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full', s.live ? 'bg-brand-gradient text-white shadow-brand' : s.active ? 'bg-accent-soft text-accent' : 'bg-muted text-faint')}>
            {s.done && !s.live ? <Icon name="check" className="h-5 w-5" strokeWidth={2.5} /> : <Icon name={STAGE_ICON[s.stage]} className="h-[18px] w-[18px]" />}
          </span>
          <span className="min-w-0">
            <span className="eyebrow block text-[10px]">
              Stage {s.n} · {STAGE_LABEL[s.stage]}
            </span>
            <span className={cx('mt-0.5 block truncate text-sm font-medium', s.active ? 'text-fg' : 'text-faint')}>{s.note}</span>
          </span>
          {s.score !== null && <span className={cx('display ml-auto text-[26px] font-medium leading-none tabular', scoreText(s.score))}>{s.score}</span>}
        </li>
      ))}
    </ol>
  );
}

function VerdictHero({ score, verdict, recommendation, aiRecommendation, reasons }: { score: number; verdict: Verdict; recommendation: Scorecard['recommendation']; aiRecommendation: Scorecard['recommendation']; reasons?: string[] }) {
  return (
    <div className="flex items-center gap-5">
      <Ring value={score} size={104} stroke={9} tone="auto" label={`Score ${score} of 100`}>
        <span className="display text-[32px] font-medium leading-none tabular text-fg">{score}</span>
        <span className="mt-1 font-mono text-[9px] uppercase tracking-wider text-muted-fg">of 100</span>
      </Ring>
      <div className="min-w-0 flex-1">
        <span className={cx('inline-flex rounded-full px-2.5 py-0.5 text-sm font-semibold ring-1 ring-inset', VERDICT_PILL[verdict])}>{VERDICT[verdict].label}</span>
        <p className="mt-2 text-sm">
          Recommendation <strong className="font-semibold">{RECOMMENDATION[recommendation]}</strong>
          {aiRecommendation !== recommendation && <span className="text-muted-fg"> (AI said {RECOMMENDATION[aiRecommendation].toLowerCase()})</span>}
        </p>
        {reasons && reasons.length > 0 && (
          <ul className="mt-1.5 space-y-0.5 text-xs text-muted-fg">
            {reasons.map((r) => (
              <li key={r} className="flex gap-1.5">
                <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-faint" />
                {r}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** The skills assessment (stage 2): the planned tasks, and once scored, each task's score and evidence. */
function SkillsView({ interview, onJump }: { interview: InterviewDetail; onJump: (seq: number) => void }) {
  const plan = interview.plan;
  const card = interview.scorecard;
  const report = card ? (card.assessment as unknown as SkillsReport) : null;
  const logs = new Map((interview.state?.tasks ?? []).map((l) => [l.taskId, l]));
  const results = new Map((report?.tasks ?? []).map((t) => [t.taskId, t]));
  const integrity = Object.entries(interview.integrity ?? {}).filter(([k, v]) => v > 0 && !['tab_visible', 'mic_unmuted'].includes(k));
  if (!plan) return null;
  return (
    <>
      <Card title="Skills assessment" description={card ? undefined : 'Stage 2'}>
        {card && report ? (
          <VerdictHero score={card.overallScore} verdict={card.verdict} recommendation={card.recommendation} aiRecommendation={card.aiRecommendation} reasons={report.reasons} />
        ) : (
          <p className="text-sm text-muted-fg">
            {interview.status === 'IN_PROGRESS'
              ? 'The assessment is running now.'
              : interview.status === 'COMPLETED' || interview.status === 'ABANDONED'
                ? 'Finished; being scored.'
                : `${INTERVIEW_STATUS[interview.status].label}${interview.scheduledAt ? ` · booked for ${formatDateTime(interview.scheduledAt)}` : ''}. ${plan.tasks.length} tasks planned, about ${plan.minutes} minutes.`}
          </p>
        )}
        {report?.summary && <p className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-fg-2">{report.summary}</p>}
      </Card>

      <Card title={`Tasks (${plan.tasks.length})`}>
        <ol className="space-y-5">
          {plan.tasks.map((t) => {
            const r = results.get(t.taskId);
            const log = logs.get(t.taskId);
            const events = Object.entries(log?.integrity ?? {}).filter(([k, v]) => v > 0 && k !== 'tab_visible');
            return (
              <li key={t.taskId}>
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="font-semibold text-fg">
                    {t.n}. {t.title}
                  </span>
                  <span className="shrink-0 font-mono text-xs text-muted-fg">{r?.score != null ? `${r.score} / 5` : log?.endedAt ? 'not scored' : log?.startedAt ? 'started' : 'not reached'}</span>
                </div>
                <p className="text-xs text-muted-fg">
                  {TASK_KIND_LABEL[t.kind]}
                  {t.kind === 'roleplay' && isLanguageCode(t.language) && ` · in ${LANGUAGES[t.language].label}`}
                  {r?.languageFluency != null && ` · fluency ${r.languageFluency}/5`}
                  {log?.endReason && ` · ended: ${log.endReason === 'time' ? 'time ran out' : log.endReason}`}
                </p>
                {r?.score != null && <ProgressBar value={r.score} max={5} size="sm" className="mt-2" />}
                <div className="mt-2 space-y-1 text-xs text-muted-fg">
                  {r?.rationale && <p>{r.rationale}</p>}
                  {r?.evidence.map((e, i) => (
                    <Evidence key={i} quote={e.quote} onClick={() => onJump(e.turn)} />
                  ))}
                  {(log?.notes.length ?? 0) > 0 && <p>Assessor noted: {log!.notes.join(' · ')}</p>}
                  {events.length > 0 && <p className="text-warning">Page events: {events.map(([k, v]) => `${k.replace(/_/g, ' ')} ×${v}`).join(', ')}</p>}
                  {log?.written && (
                    <details className="rounded-md bg-surface-2 px-3 py-2 text-fg ring-1 ring-border">
                      <summary className="cursor-pointer text-xs font-semibold">Written answer ({log.written.trim().split(/\s+/).length} words)</summary>
                      <p className="mt-2 whitespace-pre-wrap text-sm">{log.written}</p>
                    </details>
                  )}
                  <details>
                    <summary className="cursor-pointer">What good looks like</summary>
                    <p className="mt-1">{t.yardstick}</p>
                  </details>
                </div>
              </li>
            );
          })}
        </ol>
      </Card>

      {report && (
        <Card title="Assessment notes">
          <div className="space-y-4">
            <NoteList title="Strengths" items={report.strengths} dot="bg-success" />
            <NoteList title="Concerns" items={report.concerns} dot="bg-warning" />
            <NoteList title="Integrity (for you to judge)" items={report.integrityNotes} dot="bg-destructive" />
          </div>
          <p className="mt-4 border-t border-border pt-3 text-xs text-muted-fg">
            {integrity.length > 0 ? <>Page events: {integrity.map(([k, v]) => `${k.replace(/_/g, ' ')} ×${v}`).join(', ')}</> : 'No page events recorded'}
            {card && <> · {card.model}</>}
          </p>
        </Card>
      )}
    </>
  );
}

function Evidence({ quote, onClick }: { quote: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="group flex w-full gap-2 rounded-[8px] px-2 py-1 text-left transition hover:bg-accent-soft" title="Play this moment">
      <Icon name="play" className="mt-0.5 h-3 w-3 shrink-0 text-accent" />
      <span className="italic text-fg-2 group-hover:text-fg">“{quote}”</span>
    </button>
  );
}

function Detail({ label, value, extra }: { label: string; value: string; extra?: string | undefined }) {
  return (
    <div className="flex justify-between gap-4 py-2 first:pt-0 last:pb-0">
      <dt className="text-muted-fg">{label}</dt>
      <dd className="text-right text-fg">
        {value}
        {extra && <div className="text-xs text-muted-fg">{extra}</div>}
      </dd>
    </div>
  );
}

function NoInterviewNote({ candidate }: { candidate: CandidateDetail }) {
  const latest = candidate.interviews[0];
  return (
    <p className="text-sm text-muted-fg">
      {!latest
        ? 'No interview yet. Select this candidate on the job page and start an interview.'
        : candidate.status === 'INTERVIEWED'
          ? 'The interview is finished and is being scored now.'
          : `Latest attempt: ${INTERVIEW_STATUS[latest.status].label.toLowerCase()}. A scorecard appears here once an interview is completed.`}
    </p>
  );
}

// ---- replay ----------------------------------------------------------------------------------

const RATES = [1, 1.5, 2];

function ReplayCard({ interview, card, seek }: { interview: InterviewDetail; card: Scorecard | null; seek: SeekRequest | null }) {
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const transcriptRef = useRef<HTMLOListElement | null>(null);
  const segIdx = useRef(-1);
  const [time, setTime] = useState(0);
  const [mediaMs, setMediaMs] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [mediaError, setMediaError] = useState(false);

  const segs = useMemo(() => buildClipSegments(interview.turns), [interview.turns]);
  const mode: 'recording' | 'clips' | 'none' = interview.recordingUrl ? 'recording' : segs.length > 0 ? 'clips' : 'none';
  const isVideo = mode === 'recording' && interview.channel === 'VIDEO';
  const duration = timelineDuration(interview, mode === 'recording' ? mediaMs : null);
  const markers = useMemo(() => computeMarkers(interview, card, duration), [interview, card, duration]);
  const active = activeTurnSeq(interview.turns, time);

  // A different interview: stop and rewind.
  useEffect(() => {
    mediaRef.current?.pause();
    segIdx.current = -1;
    setTime(0);
    setMediaMs(null);
    setPlaying(false);
    setMediaError(false);
  }, [interview.id]);

  const loadSegment = (i: number, withinMs: number, autoplay: boolean) => {
    const el = mediaRef.current;
    const s = segs[i];
    if (!el || !s) return;
    segIdx.current = i;
    const apply = () => {
      el.currentTime = withinMs / 1000;
      if (autoplay) void el.play().catch(() => setPlaying(false));
    };
    if (el.getAttribute('src') !== s.src) {
      el.addEventListener('loadedmetadata', apply, { once: true });
      el.src = s.src;
      el.load();
    } else {
      apply();
    }
  };

  const seekTo = (ms: number, autoplay = playing) => {
    const el = mediaRef.current;
    const t = Math.max(0, Math.min(ms, duration));
    if (mode === 'recording' && el) {
      el.currentTime = t / 1000;
      setTime(t);
      if (autoplay) void el.play().catch(() => setPlaying(false));
    } else if (mode === 'clips') {
      const loc = locateSegment(segs, t);
      if (!loc) {
        el?.pause();
        setTime(duration);
        return;
      }
      setTime(segs[loc.index]!.start + loc.withinMs);
      loadSegment(loc.index, loc.withinMs, autoplay);
    } else {
      setTime(t);
    }
  };

  const play = () => {
    const el = mediaRef.current;
    if (!el) return;
    const from = time >= duration - 250 ? 0 : time;
    if (mode === 'recording') {
      if (from !== time) el.currentTime = 0;
      void el.play().catch(() => setPlaying(false));
    } else if (mode === 'clips') {
      seekTo(from, true);
    }
  };

  // Evidence clicked elsewhere on the page.
  const lastSeek = useRef(0);
  useEffect(() => {
    if (!seek || seek.interviewId !== interview.id || seek.n === lastSeek.current) return;
    lastSeek.current = seek.n;
    const at = turnOffset(interview.turns, seek.seq);
    if (at !== null) seekTo(at, mode !== 'none');
    // seekTo is recreated each render; the request counter is what matters here.
  }, [seek, interview.id]);

  // Keep the spoken line in view inside the transcript while playing.
  useEffect(() => {
    if (!playing || active === null) return;
    const box = transcriptRef.current;
    const item = box?.querySelector<HTMLElement>(`[data-seq="${active}"]`);
    if (box && item) box.scrollTo({ top: item.offsetTop - box.clientHeight / 2 + item.clientHeight / 2, behavior: 'smooth' });
  }, [active, playing]);

  const mediaEvents = {
    onPlay: () => setPlaying(true),
    onPause: () => setPlaying(false),
    onError: () => setMediaError(true),
    onLoadedMetadata: (e: { currentTarget: HTMLMediaElement }) => {
      const d = e.currentTarget.duration;
      if (mode === 'recording' && Number.isFinite(d)) setMediaMs(d * 1000);
      e.currentTarget.playbackRate = rate;
    },
    onTimeUpdate: (e: { currentTarget: HTMLMediaElement }) => {
      const el = e.currentTarget;
      if (mode === 'recording') setTime(el.currentTime * 1000);
      else {
        const s = segs[segIdx.current];
        if (s) setTime(Math.min(s.end, s.start + el.currentTime * 1000));
      }
    },
    onEnded: () => {
      if (mode !== 'clips') return setPlaying(false);
      const next = segIdx.current + 1;
      const s = segs[next];
      if (s) {
        setTime(s.start);
        loadSegment(next, 0, true);
      } else {
        setPlaying(false);
        setTime(duration);
      }
    },
  };

  const setMediaRef = (el: HTMLMediaElement | null) => {
    mediaRef.current = el;
  };

  const changeRate = () => {
    const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length]!;
    setRate(next);
    if (mediaRef.current) {
      mediaRef.current.playbackRate = next;
      mediaRef.current.defaultPlaybackRate = next;
    }
  };

  const onBarClick = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    seekTo(((e.clientX - r.left) / r.width) * duration);
  };

  const pct = duration > 0 ? Math.min(100, (time / duration) * 100) : 0;
  const st = INTERVIEW_STATUS[interview.status];
  const speakerName = (s: 'AI' | 'CANDIDATE' | 'SYSTEM') => (s === 'AI' ? (interview.stage === 'ASSESSMENT' ? 'Assessor' : 'Interviewer') : s === 'CANDIDATE' ? 'Candidate' : 'System');

  return (
    <section id="replay" className="surface scroll-mt-24 overflow-hidden rounded-lg">
      <header className="flex flex-wrap items-center justify-between gap-2 px-5 py-4">
        <h2 className="flex flex-wrap items-center gap-2 text-[15px] font-semibold tracking-tight text-fg">
          <Icon name="headphones" className="h-4 w-4 text-accent" />
          <span>
            {interview.stage === 'SCREENING' ? (CHANNEL_LABEL[interview.channel] ?? interview.channel) : STAGE_LABEL[interview.stage]} #{interview.attemptNo}
          </span>
          <Badge tone={st.tone} size="xs">
            {st.label}
          </Badge>
        </h2>
        <span className="text-xs text-muted-fg">
          {formatDateTime(interview.startedAt ?? interview.createdAt)}
          {interview.language && isLanguageCode(interview.language) && <> · {LANGUAGES[interview.language].label}</>}
        </span>
      </header>

      <div className="bg-night px-5 py-5 text-white">
        {isVideo ? (
          <video ref={setMediaRef} src={interview.recordingUrl!} className="mb-4 aspect-video w-full rounded-md bg-black" playsInline preload="metadata" onClick={() => (playing ? mediaRef.current?.pause() : play())} {...mediaEvents} />
        ) : mode === 'recording' ? (
          <audio ref={setMediaRef} src={interview.recordingUrl!} preload="metadata" {...mediaEvents} />
        ) : (
          <audio ref={setMediaRef} preload="none" {...mediaEvents} />
        )}
        <div className="flex items-center gap-4">
          <button
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-white shadow-brand transition hover:brightness-110 disabled:opacity-40"
            disabled={mode === 'none'}
            onClick={() => (playing ? mediaRef.current?.pause() : play())}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            <Icon name={playing ? 'pause' : 'play'} className="h-5 w-5" strokeWidth={2} />
          </button>
          <div className="min-w-0 flex-1">
            <div className="relative h-6 cursor-pointer" onClick={onBarClick} role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={Math.round(duration / 1000)} aria-valuenow={Math.round(time / 1000)}>
              <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-white/15">
                <div className="h-full bg-brand-gradient" style={{ width: `${pct}%` }} />
              </div>
              {markers.map((m, i) => (
                <button
                  key={i}
                  className="absolute top-0 h-6 w-2 -translate-x-1/2"
                  style={{ left: `${duration > 0 ? (m.atMs / duration) * 100 : 0}%` }}
                  title={`${MARKER_STYLE[m.kind].label} · ${formatDuration(m.atMs)}${m.kind === 'consent' ? '' : ` · ${m.label}`}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    seekTo(m.atMs);
                  }}
                >
                  <span className="mx-auto block h-full w-[3px] rounded-full" style={{ background: MARKER_STYLE[m.kind].colour }} />
                </button>
              ))}
            </div>
            <div className="mt-1 flex justify-between font-mono text-[11px] tabular text-white/60">
              <span>{formatDuration(time)}</span>
              <span>{formatDuration(duration)}</span>
            </div>
          </div>
          <button className="rounded-[8px] px-2.5 py-1 font-mono text-xs text-white/70 ring-1 ring-white/20 transition hover:text-white" onClick={changeRate} aria-label="Playback speed">
            {rate}×
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/60">
          {(['consent', 'evidence', 'concern', 'flag'] as const)
            .filter((k) => markers.some((m) => m.kind === k))
            .map((k) => (
              <span key={k} className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: MARKER_STYLE[k].colour }} />
                {MARKER_STYLE[k].label}
              </span>
            ))}
          <span className="ml-auto">
            {mode === 'recording' ? 'Full recording' : mode === 'clips' ? `Stitched from ${segs.length} clips` : 'No audio kept · transcript only'}
            {mediaError && ' · audio failed to load'}
          </span>
        </div>
      </div>

      {interview.turns.length > 0 ? (
        <ol ref={transcriptRef} className="scrollbar-thin relative max-h-[520px] space-y-0.5 overflow-y-auto px-3 py-3">
          {interview.turns.map((t) => {
            const on = t.seq === active && (playing || time > 0);
            return (
              <li key={t.id} data-seq={t.seq}>
                <button
                  className={cx(
                    // Phones: time and speaker on one line, the words below at full width.
                    'flex w-full flex-wrap gap-x-3 gap-y-0.5 rounded-md px-3 py-2 text-left text-sm transition sm:flex-nowrap',
                    on ? 'bg-accent-soft ring-1 ring-accent/30' : 'hover:bg-surface-2',
                    t.speaker === 'SYSTEM' && 'text-warning',
                  )}
                  onClick={() => seekTo(t.offsetMs, mode !== 'none')}
                >
                  <span className="w-10 shrink-0 pt-0.5 font-mono text-[11px] tabular text-faint">{formatDuration(t.offsetMs)}</span>
                  <span className={cx('w-20 shrink-0 pt-0.5 text-xs font-semibold', t.speaker === 'CANDIDATE' ? 'text-pink' : t.speaker === 'AI' ? 'text-purple dark:text-accent' : 'text-warning')}>{speakerName(t.speaker)}</span>
                  <span className="min-w-0 flex-1 basis-full whitespace-pre-wrap leading-relaxed text-fg-2 sm:basis-auto">
                    {t.text}
                    {(t.typed || t.interrupted) && <span className="ml-2 font-mono text-[10px] uppercase tracking-wide text-faint">{[t.typed && 'typed', t.interrupted && 'interrupted'].filter(Boolean).join(' · ')}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="px-5 py-6 text-sm text-muted-fg">No transcript for this attempt.</p>
      )}
    </section>
  );
}

function AttemptsCard({ interviews, current, onReplay }: { interviews: InterviewDetail[]; current: string | null; onReplay: (id: string) => void }) {
  return (
    <Card title={`Attempts (${interviews.length})`}>
      <ol className="divide-y divide-border">
        {interviews.map((i) => {
          const st = INTERVIEW_STATUS[i.status];
          const duration = i.startedAt && i.endedAt ? formatDuration(new Date(i.endedAt).getTime() - new Date(i.startedAt).getTime()) : null;
          const replayable = i.turns.length > 0 || !!i.recordingUrl;
          return (
            <li key={i.id} className="py-3.5 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-fg">
                  {i.stage === 'SCREENING' ? (CHANNEL_LABEL[i.channel] ?? i.channel) : STAGE_LABEL[i.stage]} #{i.attemptNo}
                </span>
                <Badge tone={st.tone} size="xs">
                  {st.label}
                </Badge>
                {i.scorecard && <span className={cx('font-mono text-xs font-semibold', scoreText(i.scorecard.overallScore))}>score {i.scorecard.overallScore}</span>}
                <span className="ml-auto">
                  {replayable &&
                    (i.id === current ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-accent">
                        <Icon name="headphones" className="h-3.5 w-3.5" /> In the player
                      </span>
                    ) : (
                      <Button size="xs" variant="soft" icon="play" onClick={() => onReplay(i.id)}>
                        Replay
                      </Button>
                    ))}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-fg">
                <span>Created {formatDateTime(i.createdAt)}</span>
                {i.scheduledAt && <span>Booked for {formatDateTime(i.scheduledAt)}</span>}
                {duration && <span>Duration {duration}</span>}
                {i.consentAt && <span>Consent recorded</span>}
                {i.endReason && <span>Ended: {i.endReason.replace(/_/g, ' ')}</span>}
                {i.expiresAt && i.channel !== 'PHONE' && <span>Link expires {formatDateTime(i.expiresAt)}</span>}
              </div>
              {i.link && <p className="mt-2 break-all rounded-[8px] bg-surface-2 px-2.5 py-1.5 font-mono text-[11px] text-muted-fg ring-1 ring-border">{i.link}</p>}
              {(i.state?.flags?.length ?? 0) > 0 && <p className="mt-1.5 text-xs text-warning">Interviewer flags: {i.state!.flags!.join(' · ')}</p>}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

// ---- scorecard -------------------------------------------------------------------------------

function ScorecardView({ card, candidate, onJump }: { card: Scorecard; candidate: CandidateDetail; onJump: (turn: number) => void }) {
  const a = card.assessment;
  const cfg = candidate.job.config;
  const byKey = new Map(a.competencies.map((x) => [x.key, x]));
  const integrity = Object.entries(a.integrity ?? {}).filter(([, v]) => v > 0);

  return (
    <>
      <Card title="AI interview" description="Stage 1 · scored against the job's rubric">
        <VerdictHero score={card.overallScore} verdict={card.verdict} recommendation={card.recommendation} aiRecommendation={card.aiRecommendation} reasons={a.reasons} />
        {a.summary && <p className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-fg-2">{a.summary}</p>}
        <p className="mt-3 font-mono text-[11px] text-faint">
          {card.model} · {formatDateTime(card.createdAt)}
        </p>
      </Card>

      <Card title="Competencies" description="Judged 1 to 5, with the words that earned the score.">
        <div className="space-y-5">
          {cfg.competencies.map((comp) => {
            const x = byKey.get(comp.key);
            return (
              <div key={comp.key}>
                <div className="mb-1.5 flex items-baseline justify-between gap-2 text-sm">
                  <span className="font-semibold text-fg">
                    {comp.label} <span className="font-mono text-[11px] font-normal text-faint">× {comp.weight}</span>
                  </span>
                  <span className="font-mono text-xs text-muted-fg">{x?.score != null ? `${x.score} / 5` : 'not assessed'}</span>
                </div>
                <ProgressBar value={x?.score ?? 0} max={5} size="sm" />
                {x && (
                  <div className="mt-2 text-xs text-muted-fg">
                    {x.rationale && <p className="leading-relaxed">{x.rationale}</p>}
                    {x.evidence.map((e, i) => (
                      <Evidence key={i} quote={e.quote} onClick={() => onJump(e.turn)} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Facts & rules" description="Established in the conversation; the rules are applied by code.">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border">
            {cfg.facts.map((f) => {
              const v = a.typedFacts?.[f.key];
              const rules = card.knockouts.filter((k) => k.fact === f.key);
              const fact = a.facts.find((x) => x.key === f.key);
              return (
                <tr key={f.key}>
                  <td className="w-2/5 py-2.5 pr-2 align-top text-muted-fg">{f.label}</td>
                  <td className="py-2.5 align-top text-fg">
                    {v === null || v === undefined ? <span className="text-faint">not established</span> : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : `${v}${f.unit ? ` ${f.unit}` : ''}`}
                    {fact?.note && <div className="text-xs text-muted-fg">{fact.note}</div>}
                    {fact?.turn != null && (
                      <button className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline" onClick={() => onJump(fact.turn!)}>
                        <Icon name="play" className="h-3 w-3" /> hear it
                      </button>
                    )}
                  </td>
                  <td className="py-2.5 pl-2 text-right align-top">
                    {rules.map((r) => (
                      <span
                        key={r.message}
                        title={r.message}
                        className={cx(
                          'ml-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
                          r.outcome === 'passed' ? VERDICT_PILL.QUALIFIED : r.outcome === 'failed' ? (r.severity === 'hard' ? VERDICT_PILL.NOT_QUALIFIED : VERDICT_PILL.NEEDS_REVIEW) : 'bg-muted text-muted-fg ring-border-strong',
                        )}
                      >
                        {r.outcome === 'passed' ? 'OK' : r.outcome === 'failed' ? `Failed · ${r.message}` : 'Unknown'}
                      </span>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card title="Notes">
        <div className="space-y-4">
          <NoteList title="Strengths" items={a.strengths} dot="bg-success" />
          <NoteList title="Concerns" items={a.concerns} dot="bg-warning" />
          <NoteList title="Red flags" items={a.redFlags} dot="bg-destructive" />
          <NoteList title="Open questions" items={a.openQuestions} dot="bg-purple" />
        </div>
        <p className="mt-4 border-t border-border pt-3 text-xs text-muted-fg">
          English {a.englishLevel.replace(/_/g, ' ')} · Coverage {Math.round((a.coverage ?? 0) * 100)}% · Interview {a.completeness}
          {' · '}
          {integrity.length > 0 ? <>Integrity signals: {integrity.map(([k, v]) => `${k.replace(/_/g, ' ')} ×${v}`).join(', ')}</> : 'No integrity signals'}
        </p>
      </Card>
    </>
  );
}

function NoteList({ title, items, dot }: { title: string; items: string[]; dot: string }) {
  if (!items?.length) return null;
  return (
    <div>
      <h4 className="mb-1.5 text-sm font-semibold text-fg">{title}</h4>
      <ul className="space-y-1 text-sm text-fg-2">
        {items.map((x) => (
          <li key={x} className="flex gap-2.5">
            <span className={cx('mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full', dot)} />
            <span className="leading-relaxed">{x}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DecisionDialog({
  kind,
  hasAssessment,
  messages,
  email,
  onClose,
  onSubmit,
}: {
  kind: DecisionKind | null;
  hasAssessment: boolean;
  messages: JobConfig['messages'] | undefined;
  email: string | null;
  onClose: () => void;
  onSubmit: (reason: string, notify: boolean | undefined) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const d = kind ? DECISION[kind] : null;
  // Rejecting and selecting can email the candidate, in the job's wording, after the job's delay.
  const emailKind = kind === 'REJECT' ? 'reject' : kind === 'SELECT' ? 'select' : null;
  const setting = emailKind ? (messages?.[emailKind] ?? { send: true, delayHours: emailKind === 'reject' ? 24 : 0 }) : null;
  const sendEmail = setting ? (notify ?? setting.send) : false;
  const label = d?.label ?? '';
  const help = kind === 'SHORTLIST' && !hasAssessment ? 'This job has no skills assessment: the candidate is marked shortlisted.' : d?.help;
  return (
    <Modal
      open={kind !== null}
      onClose={onClose}
      title={kind === 'ADVANCE' ? label : `${label} candidate`}
      description={help}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={kind === 'REJECT' ? 'danger' : 'primary'}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              await onSubmit(reason, setting && email ? sendEmail : undefined);
              setBusy(false);
              setReason('');
              setNotify(null);
            }}
          >
            {label}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {setting && (
          <Checkbox
            checked={Boolean(email) && sendEmail}
            disabled={!email}
            onChange={setNotify}
            hint={email ? "Changing the decision before it goes out cancels it. The wording is in the job's settings, under Emails." : 'There is no email address for this candidate.'}
          >
            {emailKind === 'reject' ? 'Email them a polite rejection' : 'Email them the good news'}, {setting.delayHours === 0 ? 'right away' : `after ${setting.delayHours} hours`}
          </Checkbox>
        )}
        <label className="block text-sm">
          <span className="mb-1.5 block text-[13px] font-semibold text-fg">Reason</span>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional · kept in the audit trail" />
        </label>
      </div>
    </Modal>
  );
}
